import { formatNumber, formatNumberExact } from '../MetricCell/format';

/**
 * Formatting an ad-hoc analyst result (spec 006 T070/T073, FR-A16).
 *
 * The product's existing rules, applied to columns nobody declared in advance:
 *
 *  - `—` for null, **never `0`** (FR-055). A zero-row answer is an empty state with a sentence,
 *    not a table of dashes.
 *  - measures compact at 1,000 to exactly two decimals (`COMPACT_FROM`), with every digit kept in
 *    the accessible name and in the clipboard copy.
 *  - ⚠ **identifiers never compact.**
 */

export type ColumnRole = 'measure' | 'identifier' | 'date' | 'label' | 'ratio';

export interface ColumnSpec {
  key: string;
  /** The short code — the VISIBLE header text. */
  label: string;
  /** The full meaning. ⚠ Required: gate 2 exempts no column. */
  fullLabel: string;
  role: ColumnRole;
}

/**
 * The face of one cell.
 *
 * ⚠ **The role decides, never the value.** A `job_id` of 1,043,221 must render `1043221`, not
 * `1.04M` — and a formatter that inspected the number would get that right on every sample where
 * ids happen to be small, then silently turn an identifier into a magnitude the day they are not.
 */
export function formatCell(value: unknown, role: ColumnRole): string {
  // FR-055: a missing figure is a dash, never a zero. They mean different things and only one of
  // them is a number.
  if (value === null || value === undefined || value === '') return '—';

  switch (role) {
    case 'measure':
      return typeof value === 'number' ? formatNumber(value) : String(value);

    case 'ratio':
      return typeof value === 'number' ? `${formatNumberExact(value)}%` : String(value);

    case 'identifier':
      /*
       * ⚠ Exact, always, and deliberately not `formatNumberExact` either: that inserts thousands
       * separators, and `1,043,221` is not a job id you can paste back into anything.
       */
      return String(value);

    case 'date':
      return formatDate(value);

    default:
      return plainFace(value);
  }
}

/**
 * A label cell's face, for the values a warehouse row actually contains.
 *
 * ⚠ **`true`/`false` is developer output, not a reading.** Nine of a placement's twenty-one
 * columns are booleans (`is_offer`, `is_start`, `is_active_at_asof` …) and a table of `false` is
 * a table nobody scans. `Yes`/`No`, and **never `—` for `false`** — a dash means the value is
 * missing (FR-055) and `false` is a statement that it is not.
 *
 * ⚠ **An array is joined, not stringified.** `String(['R1','R3'])` gives `R1,R3` with no space,
 * which reads as one token. `jobs.ui_regions` is a real list — the FR-070 fan-out — and a reader
 * has to be able to see it is two regions.
 */
function plainFace(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  return String(value);
}

/**
 * The value for the accessible name and the clipboard.
 *
 * ⚠ **Every digit, always.** `20.14K` pasted into a spreadsheet is text, not a number — the same
 * divergence the product already makes between a table face and its accessible name.
 */
export function exactCell(value: unknown, role: ColumnRole): string {
  if (value === null || value === undefined || value === '') return '';
  if (role === 'measure' && typeof value === 'number') return formatNumberExact(value);
  if (role === 'date') return formatDate(value);
  /*
   * ⚠ The same face as the screen for these two. A clipboard that said `true` where the table
   * said `Yes` would make a pasted table disagree with the one it was copied from — and the
   * export's whole promise is that it holds exactly what was on screen.
   */
  return plainFace(value);
}

function formatDate(value: unknown): string {
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  // ⚠ UTC. All date arithmetic in this product is UTC, and a local-zone render moves a row
  // across a day boundary for readers west of the meridian.
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * `9,122 rows · showing 500 · export carries all 9,122`.
 *
 * ⚠ The count is always the TOTAL, and the export's promise is stated **before** the reader
 * clicks: the export is not "what is on screen", and this is the only place that says so.
 */
export function rowCountFace(total: number, displayed: number): string {
  const n = (v: number) => v.toLocaleString('en-US');
  if (displayed >= total) return `${n(total)} row${total === 1 ? '' : 's'}`;
  return `${n(total)} rows · showing ${n(displayed)} · export carries all ${n(total)}`;
}

/**
 * The partial-bucket mark (FR-A21).
 *
 * ⚠ A trailing `*` on the label, and the reason in the full label. A period sum dives at the
 * right edge for no reason but the period being days old; the chart dashes it, and a table that
 * draws it like any other bucket is the two readings of one snapshot disagreeing about the one
 * thing they must agree on.
 */
export function partialBucketLabel(label: string): string {
  return `${label}*`;
}

export const PARTIAL_BUCKET_NOTE = 'the last bucket is still in progress';
